import React, { useState, useEffect, useMemo } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { categoriesApi, subcategoriesApi } from "../../../services";
import type { CategoryAttributes } from "../../../types";
import type { SubcategoryAttributes } from "../../../types";
import type { CategoryType } from "../../../types";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  Loading,
  ErrorState,
  IconsLarge,
  Select,
  type SelectOption,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { CreateSubcategoryModal } from "./CreateSubcategoryModal";
import { AllSubcategoriesListView } from "./AllSubcategoriesListView";

const TYPE_FILTER_OPTIONS: SelectOption[] = [
  { value: "", label: "Todos" },
  { value: "input", label: "Entrada" },
  { value: "output", label: "Saída" },
];

/** Valor sentinela do filtro "Sem plano" (Select não aceita `null` como value). */
const PLAN_FILTER_NONE = "__plan_none__";

export const Category: React.FC = () => {
  const { company } = useCompany();
  const companyId = company?.id ?? 0;
  const [categories, setCategories] = useState<CategoryAttributes[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateSubcategory, setShowCreateSubcategory] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<SubcategoryAttributes | null>(null);
  const [showDeleteSubcategoryModal, setShowDeleteSubcategoryModal] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<number | null>(null);
  const [deleteSubcategoryLoading, setDeleteSubcategoryLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | number>("");
  const [motherCategoryFilter, setMotherCategoryFilter] = useState<string | number>("");
  const [planFilter, setPlanFilter] = useState<string | number>("");
  const [searchQuery, setSearchQuery] = useState("");

  const motherCategoryOptions: SelectOption[] = useMemo(() => {
    const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    return [{ value: "", label: "Todas" }, ...sorted.map((c) => ({ value: c.id, label: c.name }))];
  }, [categories]);

  const planFilterOptions: SelectOption[] = useMemo(() => {
    const plans = new Set<string>();
    for (const c of categories) {
      const p = c.plan?.trim();
      if (p) plans.add(p);
    }
    const sorted = Array.from(plans).sort((a, b) => a.localeCompare(b, "pt-BR"));
    const opts: SelectOption[] = [{ value: "", label: "Todos" }];
    const hasEmptyPlan = categories.some((c) => !(c.plan?.trim() ?? ""));
    if (hasEmptyPlan) opts.push({ value: PLAN_FILTER_NONE, label: "Sem plano" });
    opts.push(...sorted.map((p) => ({ value: p, label: p })));
    return opts;
  }, [categories]);

  const filteredSubcategories = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    return subcategories.filter((s) => {
      if (motherCategoryFilter !== "" && motherCategoryFilter !== undefined) {
        const mid =
          typeof motherCategoryFilter === "number"
            ? motherCategoryFilter
            : Number(motherCategoryFilter);
        if (!Number.isNaN(mid) && s.categoryId !== mid) return false;
      }
      if (planFilter !== "" && planFilter !== undefined) {
        const cat = categories.find((c) => c.id === s.categoryId);
        const planStr = (cat?.plan?.trim() ?? "") || "";
        if (planFilter === PLAN_FILTER_NONE) {
          if (planStr !== "") return false;
        } else if (planStr !== String(planFilter)) {
          return false;
        }
      }
      if (typeFilter === "input" || typeFilter === "output") {
        const cat = categories.find((c) => c.id === s.categoryId);
        if (cat?.type !== (typeFilter as CategoryType)) return false;
      }
      if (term) {
        const cat = categories.find((c) => c.id === s.categoryId);
        const haystack = [s.name, cat?.name ?? "", cat?.plan ?? ""].join(" ").toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [subcategories, categories, motherCategoryFilter, planFilter, typeFilter, searchQuery]);

  const hasListFilters =
    typeFilter !== "" ||
    motherCategoryFilter !== "" ||
    planFilter !== "" ||
    searchQuery.trim() !== "";

  const fetchData = async () => {
    if (companyId === 0) {
      setCategories([]);
      setSubcategories([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [catRes, subRes] = await Promise.all([
        categoriesApi.getAll(companyId),
        subcategoriesApi.getAll(companyId),
      ]);
      const loadErrors: string[] = [];
      if (catRes.success && catRes.data) {
        setCategories(catRes.data);
      } else {
        setCategories([]);
        loadErrors.push(catRes.error ?? "Falha ao carregar categorias");
      }
      if (subRes.success && subRes.data) {
        setSubcategories(subRes.data);
      } else {
        setSubcategories([]);
        loadErrors.push(subRes.error ?? "Falha ao carregar categorias");
      }
      if (loadErrors.length > 0) setError(loadErrors.join(" — "));
    } catch {
      setError("Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const handleSubcategorySaved = () => {
    fetchData();
    setShowCreateSubcategory(false);
    setEditingSubcategory(null);
  };

  const handleDeleteSubcategory = (id: number) => {
    setSubcategoryToDelete(id);
    setShowDeleteSubcategoryModal(true);
  };

  const confirmDeleteSubcategory = async () => {
    if (!subcategoryToDelete) return;
    setDeleteSubcategoryLoading(true);
    setError(null);
    try {
      const response = await subcategoriesApi.delete(subcategoryToDelete);
      if (response.success) {
        await fetchData();
        setShowDeleteSubcategoryModal(false);
        setSubcategoryToDelete(null);
      } else {
        setError(response.error ?? "Falha ao excluir categoria");
      }
    } catch {
      setError("Falha ao excluir categoria");
    } finally {
      setDeleteSubcategoryLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<IconsLarge.Categories />}
        title="Categorias"
        subtitle="Categorias por empresa, com categoria mãe, tipo e plano"
        showViewToggle={false}
        buttonText="Nova categoria"
        onButtonClick={() => {
          setEditingSubcategory(null);
          setShowCreateSubcategory(true);
        }}
        buttonDisabled={loading}
      />

      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => fetchData()}
        />
      )}

      <CreateSubcategoryModal
        isOpen={showCreateSubcategory}
        onClose={() => {
          setShowCreateSubcategory(false);
          setEditingSubcategory(null);
        }}
        onSuccess={handleSubcategorySaved}
        companyId={companyId}
        categories={categories}
        editingSubcategory={editingSubcategory}
      />

      {loading && !showCreateSubcategory ? (
        <Loading
          message="Carregando categorias..."
          size="xl"
        />
      ) : (
        <>
          {subcategories.length > 0 && (
            <Card className="p-6">
              <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                Filtros
                {hasListFilters && (
                  <button
                    type="button"
                    onClick={() => {
                      setTypeFilter("");
                      setMotherCategoryFilter("");
                      setPlanFilter("");
                      setSearchQuery("");
                    }}
                    className="text-sky-600 dark:text-[#63B3ED] hover:text-sky-800 dark:hover:text-blue-300 underline font-normal"
                  >
                    Limpar
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl">
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                    Buscar
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-slate-500">
                      🔍
                    </span>
                    <input
                      type="text"
                      placeholder="Categoria ou categoria mãe"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={loading}
                      className="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-[#374151] rounded-lg bg-white dark:bg-[#2D3748] dark:text-slate-200 dark:placeholder-slate-500 focus:ring-2 focus:ring-sky-500 dark:focus:ring-[#63B3ED] focus:border-sky-500 dark:focus:border-[#63B3ED] disabled:bg-gray-50 dark:disabled:bg-slate-700 disabled:cursor-not-allowed"
                      aria-label="Buscar por categoria ou categoria mãe"
                    />
                  </div>
                </div>
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                    Tipo
                  </label>
                  <Select
                    value={typeFilter}
                    onChange={(v) => setTypeFilter(v)}
                    options={TYPE_FILTER_OPTIONS}
                    disabled={loading}
                    searchable={false}
                    className="text-sm"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                    Categoria mãe
                  </label>
                  <Select
                    value={motherCategoryFilter}
                    onChange={(v) => setMotherCategoryFilter(v)}
                    options={motherCategoryOptions}
                    placeholder="Todas"
                    disabled={loading}
                    searchable={motherCategoryOptions.length > 10}
                    className="text-sm"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                    Plano
                  </label>
                  <Select
                    value={planFilter}
                    onChange={(v) => setPlanFilter(v)}
                    options={planFilterOptions}
                    placeholder="Todos"
                    disabled={loading}
                    searchable={planFilterOptions.length > 12}
                    className="text-sm"
                  />
                </div>
              </div>
              {hasListFilters && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-3">
                  {filteredSubcategories.length} de {subcategories.length} categorias
                </p>
              )}
            </Card>
          )}

          <Card className="p-8">
            {subcategories.length === 0 ? (
              <EmptyState
                icon={<IconsLarge.Categories />}
                title="Nenhuma categoria encontrada"
                description="Cadastre categorias vinculadas a uma categoria mãe para organizar receitas e despesas."
                actionText="Nova categoria"
                onAction={() => setShowCreateSubcategory(true)}
              />
            ) : (
              <AllSubcategoriesListView
                subcategories={filteredSubcategories}
                categories={categories}
                onEdit={(s) => {
                  setEditingSubcategory(s);
                  setShowCreateSubcategory(true);
                }}
                onDelete={handleDeleteSubcategory}
                loading={loading}
                emptyMessage={
                  subcategories.length > 0 && filteredSubcategories.length === 0
                    ? "Nenhum resultado para os filtros selecionados."
                    : "Nenhuma categoria encontrada"
                }
              />
            )}
          </Card>
        </>
      )}

      <ConfirmationModal
        isOpen={showDeleteSubcategoryModal}
        title="Excluir categoria"
        message="Tem certeza que deseja excluir esta categoria? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDeleteSubcategory}
        onCancel={() => {
          setShowDeleteSubcategoryModal(false);
          setSubcategoryToDelete(null);
        }}
        loading={deleteSubcategoryLoading}
      />
    </div>
  );
};

export default Category;
